// src/components/NavbarComponent.js
import React, { useState, useEffect } from 'react';
import { Container, Navbar, Nav, Button, Image } from 'react-bootstrap';
import { Link, NavLink } from 'react-router-dom';
import { FaUserMd, FaPhoneAlt, FaBars, FaTimes, FaBlog, FaShieldAlt } from 'react-icons/fa';
import logo from '../media/logo.png';

const NavbarComponent = () => {
    const [expanded, setExpanded] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 30;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrolled]);

    // Navigation links with their icons and paths
    const navLinks = [
        { path: '/', label: 'Home', exact: true },
        { path: '#services', label: 'Services' },
        { path: '#doctors', label: 'Doctors' },
        { path: '#facilities', label: 'Facilities' },
        { path: '#blog', label: 'Blog' },
        { path: '/admin-login', label: 'Admin Login', isButton: true }
    ];

    const handleNavClick = () => {
        setExpanded(false);
    };

    return (
        <Navbar
            bg="primary"
            variant="dark"
            expand="lg"
            fixed="top"
            expanded={expanded}
            className={scrolled ? 'navbar-scrolled' : ''}
            style={{
                background: scrolled 
                    ? 'linear-gradient(135deg, #0c2d4a 0%, #1a3a5f 100%)'
                    : 'linear-gradient(135deg, #0c2d4a 0%, #1a3a5f 100%)',
                padding: scrolled ? '5px 0' : '8px 0',
                boxShadow: scrolled 
                    ? '0 4px 20px rgba(0, 0, 0, 0.25)'
                    : '0 2px 15px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                minHeight: '80px' // Increased min-height
            }}
        >
            <Container className="px-3 px-sm-4">
                {/* Brand Logo - Increased size */}
                <Navbar.Brand as={Link} to="/" className="d-flex align-items-center me-0 me-lg-3" onClick={handleNavClick}>
                    <Image
                        src={logo}
                        alt="Mother & Child Care Hospital"
                        style={{
                            height: '90px', // Increased from 75px
                            width: '90px', // Increased from 75px
                            filter: 'brightness(1.15) drop-shadow(0 3px 6px rgba(0,0,0,0.3))',
                            transition: 'all 0.3s ease',
                            objectFit: 'contain',
                            marginRight: '15px' // Increased margin
                        }}
                        className="desktop-logo"
                    />
                    <div className="d-flex flex-column justify-content-center">
                        <h4 className="mb-0 fw-bold text-white" style={{
                            fontSize: '1.3rem', // Slightly increased
                            lineHeight: '1.1',
                            letterSpacing: '0.3px',
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            whiteSpace: 'nowrap'
                        }}>
                            Mother & Child Care
                        </h4>
                        <small className="text-white opacity-85" style={{
                            fontSize: '0.7rem', // Slightly increased
                            letterSpacing: '0.5px',
                            fontWeight: '400',
                            marginTop: '2px',
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            whiteSpace: 'nowrap'
                        }}>
                            HEALTHCARE EXCELLENCE
                        </small>
                    </div>
                </Navbar.Brand>

                {/* Mobile Toggle Button */}
                <Navbar.Toggle
                    aria-controls="basic-navbar-nav"
                    onClick={() => setExpanded(!expanded)}
                    className="ms-auto me-2"
                    style={{
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {expanded ? (
                        <FaTimes size={18} className="text-white" />
                    ) : (
                        <FaBars size={18} className="text-white" />
                    )}
                </Navbar.Toggle>

                {/* Navigation Links - Desktop version */}
                <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
                    <Nav className="align-items-stretch align-items-lg-center mt-3 mt-lg-0">
                        {navLinks.map((link, index) => {
                            if (link.isButton) {
                                return (
                                    <div key={index} className="my-2 my-lg-0 ms-lg-2">
                                        <Button
                                            as={Link}
                                            to={link.path}
                                            variant="outline-light"
                                            className="rounded-pill w-100 d-flex align-items-center justify-content-center"
                                            onClick={handleNavClick}
                                            style={{
                                                borderWidth: '1.5px',
                                                borderColor: 'rgba(255, 255, 255, 0.25)',
                                                fontWeight: '600',
                                                fontSize: '0.9rem',
                                                transition: 'all 0.3s ease',
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                padding: '8px 16px',
                                                maxWidth: '200px',
                                                margin: '0 auto'
                                            }}
                                        >
                                            <FaShieldAlt className="me-2" style={{ fontSize: '0.8rem' }} />
                                            {link.label}
                                        </Button>
                                    </div>
                                );
                            }

                            if (link.path.startsWith('#')) {
                                return (
                                    <Nav.Link
                                        key={index}
                                        href={link.path}
                                        className="mx-2 mx-lg-3 position-relative text-center py-3 py-lg-2 px-lg-2"
                                        onClick={handleNavClick}
                                        style={{
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            fontWeight: '500',
                                            fontSize: '0.95rem',
                                            padding: '8px 4px',
                                            transition: 'all 0.3s ease',
                                            letterSpacing: '0.2px'
                                        }}
                                    >
                                        {link.icon && link.icon}
                                        {link.label}
                                        <span className="position-absolute bottom-0 start-0 w-100"
                                            style={{
                                                height: '2px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                                                transform: 'scaleX(0)',
                                                transition: 'transform 0.3s ease',
                                                borderRadius: '1px'
                                            }}
                                        />
                                    </Nav.Link>
                                );
                            }

                            return (
                                <Nav.Link
                                    key={index}
                                    as={NavLink}
                                    to={link.path}
                                    exact={link.exact}
                                    className="mx-2 mx-lg-3 position-relative text-center py-3 py-lg-2 px-lg-2"
                                    onClick={handleNavClick}
                                    style={({ isActive }) => ({
                                        color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
                                        fontWeight: isActive ? '600' : '500',
                                        fontSize: '0.95rem',
                                        padding: '8px 4px',
                                        transition: 'all 0.3s ease',
                                        letterSpacing: '0.2px'
                                    })}
                                >
                                    {link.icon && link.icon}
                                    {link.label}
                                    <span className="position-absolute bottom-0 start-0 w-100"
                                        style={{
                                            height: '2px',
                                            backgroundColor: '#20B2AA',
                                            transform: 'scaleX(0)',
                                            transition: 'transform 0.3s ease',
                                            borderRadius: '1px'
                                        }}
                                    />
                                </Nav.Link>
                            );
                        })}

                        {/* Emergency Button */}
                        <div className="my-3 my-lg-0 ms-lg-2">
                            <Button
                                className="rounded-pill w-100 d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: '#E63946',
                                    border: 'none',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 2px 8px rgba(230, 57, 70, 0.3)',
                                    transition: 'all 0.3s ease',
                                    padding: '10px 20px',
                                    maxWidth: '200px',
                                    margin: '0 auto'
                                }}
                                onClick={() => {
                                    handleNavClick();
                                    window.location.href = 'tel:+12345678900';
                                }}
                            >
                                <FaPhoneAlt className="me-2" style={{ fontSize: '0.9rem' }} />
                                Emergency
                            </Button>
                        </div>
                    </Nav>
                </Navbar.Collapse>
            </Container>

            {/* Custom CSS for hover effects */}
            <style jsx="true">{`
                /* Base styles */
                .navbar {
                    min-height: 80px; /* Increased from 70px */
                }
                
                /* Desktop styles (lg and above) */
                @media (min-width: 992px) {
                    .navbar-nav .nav-link {
                        position: relative;
                    }
                    
                    .navbar-nav .nav-link:hover {
                        color: #ffffff !important;
                    }
                    
                    .navbar-nav .nav-link:hover span {
                        transform: scaleX(1);
                    }
                    
                    /* Active link styling - underline only, no background */
                    .navbar-nav .nav-link.active {
                        color: #ffffff !important;
                        font-weight: 600 !important;
                        background-color: transparent !important;
                    }
                    
                    .navbar-nav .nav-link.active span {
                        transform: scaleX(1);
                        background-color: #20B2AA !important;
                    }
                    
                    /* Button hover effects */
                    .btn-outline-light:hover {
                        background-color: rgba(255, 255, 255, 0.1) !important;
                        border-color: rgba(255, 255, 255, 0.4) !important;
                        transform: translateY(-1px);
                    }
                    
                    /* Emergency button hover */
                    .btn[style*="background-color: #E63946"]:hover {
                        background-color: #D32F3C !important;
                        transform: translateY(-1px);
                        box-shadow: 0 4px 12px rgba(230, 57, 70, 0.4) !important;
                    }
                }
                
                /* Mobile menu styling */
                @media (max-width: 991.98px) {
                    .navbar-collapse {
                        background: linear-gradient(135deg, #0c2d4a 0%, #1a3a5f 100%);
                        padding: 15px 0;
                        margin-top: 10px;
                        border-top: 1px solid rgba(255, 255, 255, 0.1);
                        max-height: 80vh;
                        overflow-y: auto;
                    }
                    
                    .navbar-nav {
                        padding: 0 15px;
                    }
                    
                    .navbar-nav .nav-link {
                        margin: 2px 0;
                        padding: 14px 16px !important;
                        border-radius: 8px;
                        background-color: rgba(255, 255, 255, 0.05);
                        text-align: center;
                        border-bottom: none !important;
                        font-size: 1rem !important;
                    }
                    
                    .navbar-nav .nav-link:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }
                    
                    .navbar-nav .nav-link.active {
                        background-color: rgba(32, 178, 170, 0.2);
                        color: #ffffff !important;
                    }
                    
                    .navbar-nav .nav-link span {
                        display: none;
                    }
                    
                    .navbar-nav .btn {
                        margin: 8px 0;
                    }
                    
                    .navbar-nav .btn-outline-light {
                        margin-top: 10px;
                    }
                    
                    /* Emergency button on mobile */
                    .btn[style*="background-color: #E63946"] {
                        margin-top: 10px;
                        margin-bottom: 10px;
                    }
                    
                    /* Logo adjustments for mobile - Increased size */
                    .navbar-brand img {
                        height: 60px !important; /* Increased from 45px */
                        width: 60px !important; /* Increased from 45px */
                        margin-right: 10px !important; /* Increased from 8px */
                    }
                    
                    .navbar-brand h4 {
                        font-size: 1rem !important; /* Increased from 0.9rem */
                        white-space: normal !important;
                        line-height: 1.1 !important;
                    }
                    
                    .navbar-brand small {
                        font-size: 0.55rem !important; /* Increased from 0.45rem */
                        white-space: normal !important;
                    }
                    
                    .navbar-toggler {
                        padding: 5px 7px !important;
                    }
                    
                    .navbar-nav .nav-link {
                        padding: 12px 14px !important;
                        font-size: 0.95rem !important;
                    }
                    
                    .navbar-nav .btn {
                        padding: 10px 14px !important;
                        font-size: 0.9rem !important;
                    }
                }
                
                /* Logo hover effect */
                .navbar-brand:hover img {
                    transform: scale(1.05);
                    filter: brightness(1.25) drop-shadow(0 5px 10px rgba(0,0,0,0.4)) !important;
                }
                
                /* Scroll effect */
                .navbar-scrolled {
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25) !important;
                }
                
                /* Professional underline animation - desktop only */
                @media (min-width: 992px) {
                    .navbar-nav .nav-link::after {
                        content: '';
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        height: 2px;
                        background: linear-gradient(90deg, transparent, #20B2AA, transparent);
                        transform: scaleX(0);
                        transition: transform 0.3s ease;
                    }
                    
                    .navbar-nav .nav-link:hover::after {
                        transform: scaleX(1);
                    }
                }
                
                /* Desktop logo size - Larger for medium/large screens */
                @media (min-width: 992px) {
                    .navbar-brand img {
                        height: 90px !important; /* Increased from 75px */
                        width: 90px !important; /* Increased from 75px */
                        margin-right: 15px !important; /* Increased from 12px */
                    }
                    
                    .navbar-brand h4 {
                        font-size: 1.3rem !important; /* Increased from 1.2rem */
                    }
                    
                    .navbar-brand small {
                        font-size: 0.7rem !important; /* Increased from 0.65rem */
                    }
                }
                
                /* Ensure logo is always visible */
                .navbar-brand img {
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    background: none !important;
                    border: none !important;
                    border-radius: 0 !important;
                    padding: 0 !important;
                }
                
                /* Improve tap targets on mobile */
                .navbar-nav .nav-link,
                .navbar-nav .btn {
                    min-height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Hide the desktop-logo class that's not needed */
                .desktop-logo {
                    display: block !important;
                }
                
                /* Extra large screens */
                @media (min-width: 1200px) {
                    .navbar-brand img {
                        height: 95px !important;
                        width: 95px !important;
                    }
                    
                    .navbar-brand h4 {
                        font-size: 1.4rem !important;
                    }
                    
                    .navbar-brand small {
                        font-size: 0.75rem !important;
                    }
                }
            `}</style>
        </Navbar>
    );
};

export default NavbarComponent;